/**
 * Orders as the API serves them. Dates travel as ISO 8601 strings and money
 * as whole cents.
 */
import { z } from "zod";
import { ProductId } from "./catalog.js";
export const OrderId = {
    schema: z.uuid().brand(),
};
export const OrderNumber = {
    schema: z.number().int().positive().brand(),
};
export const OrderKey = {
    schema: z
        .string()
        .regex(/^[A-Za-z0-9_-]{43}$/)
        .brand(),
};
export const ClientSecret = {
    schema: z.string().min(1).brand(),
};
export const OrderStatus = {
    /** In lifecycle order, which is also the order of the `status` sort. */
    NAMES: ["pending_payment", "payment_successful", "payment_failed", "processing", "shipped", "delivered", "canceled", "refunded"],
    schema: z.enum(["pending_payment", "payment_successful", "payment_failed", "processing", "shipped", "delivered", "canceled", "refunded"]),
    label: {
        pending_payment: "Pending payment",
        payment_successful: "Payment successful",
        payment_failed: "Payment failed",
        processing: "Processing",
        shipped: "Shipped",
        delivered: "Delivered",
        canceled: "Canceled",
        refunded: "Refunded",
    },
    /** The statuses a customer may still cancel from: nothing has left the warehouse. */
    CANCELABLE: ["pending_payment", "payment_successful"],
    isCancelable: (status) => OrderStatus.CANCELABLE.includes(status),
    /** The statuses the shop may still cancel from: the parcel has not shipped. */
    STAFF_CANCELABLE: ["pending_payment", "payment_successful", "processing"],
    /** The statuses a refund takes an order from: it shipped, so canceling is too late. */
    REFUNDABLE: ["shipped", "delivered"],
    /** The statuses of an order whose payment went through. */
    PAID: ["payment_successful", "processing", "shipped", "delivered", "refunded"],
    /** The statuses of a paid order that has not shipped yet: the work of fulfillment. */
    TO_FULFILL: ["payment_successful", "processing"],
    /** Each step of fulfillment and the status before it. Staff move an order one step at a time. */
    PREVIOUS: { processing: "payment_successful", shipped: "processing", delivered: "shipped" },
};
export const OrderSort = {
    NAMES: ["newest", "oldest", "status"],
    schema: z.enum(["newest", "oldest", "status"]),
    label: { newest: "Newest first", oldest: "Oldest first", status: "Status" },
};
/** A calendar day as `YYYY-MM-DD`. */
export const CalendarDay = {
    // Postgres has no year 0, which the ISO format allows.
    schema: z.iso.date("Enter a date as YYYY-MM-DD.").refine((day) => !day.startsWith("0000"), "Enter a year from 1 on."),
};
export const OrderQueryParams = {
    schema: z
        .object({
        status: z
            .union([OrderStatus.schema, z.array(OrderStatus.schema).max(8)])
            .transform((value) => (typeof value === "string" ? [value] : value))
            .optional(),
        from: CalendarDay.schema.optional(),
        to: CalendarDay.schema.optional(),
        sort: OrderSort.schema.default("newest"),
        page: z.coerce.number().int().min(1).max(10_000).default(1),
        perPage: z.coerce.number().int().min(1).max(50).default(10),
    })
        .refine((query) => query.from === undefined || query.to === undefined || query.from <= query.to, {
        message: "The start date must not be after the end date.",
        path: ["to"],
    }),
};
//# sourceMappingURL=orders.js.map